import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Settings,
  Mail,
  Lock,
  Bell,
  Save,
  TestTube,
  Eye,
  EyeOff
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    // SMTP
    smtp_host: "",
    smtp_port: "587",
    smtp_user: "",
    smtp_password: "",
    smtp_ssl: false,
    sender_email: "",
    recipient_emails: "",
    // Cronjobs
    daily_report_enabled: false,
    expiry_alert_enabled: false,
    expiry_alert_days: "7",
    low_stock_alert_enabled: false,
    cron_secret: "snackstation2024"
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(prev => ({
        ...prev,
        ...res.data,
        smtp_port: String(res.data.smtp_port || 587),
        expiry_alert_days: String(res.data.expiry_alert_days || 7),
        recipient_emails: Array.isArray(res.data.recipient_emails) 
          ? res.data.recipient_emails.join(", ")
          : res.data.recipient_emails || ""
      }));
    } catch (err) {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...settings,
        smtp_port: parseInt(settings.smtp_port) || 587,
        expiry_alert_days: parseInt(settings.expiry_alert_days) || 7,
        recipient_emails: settings.recipient_emails
          .split(",")
          .map(e => e.trim())
          .filter(e => e)
      };
      
      // Don't send empty password
      if (!data.smtp_password) {
        delete data.smtp_password;
      }
      
      await axios.put(`${API}/settings`, data);
      toast.success("Einstellungen gespeichert");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("Passwort muss mindestens 4 Zeichen haben");
      return;
    }

    try {
      await axios.put(`${API}/settings`, {
        app_password: newPassword
      });
      toast.success("App-Passwort geändert");
      setNewPassword("");
    } catch (err) {
      toast.error("Fehler beim Ändern des Passworts");
    }
  };

  const testEmail = async () => {
    if (!settings.smtp_host || !settings.sender_email) {
      toast.error("Bitte SMTP-Einstellungen ausfüllen");
      return;
    }

    setTestingEmail(true);
    try {
      // First save settings
      await handleSave();
      
      // Then test (you'd need a test endpoint)
      toast.success("E-Mail-Einstellungen gespeichert. Testen Sie mit dem Cronjob.");
    } catch (err) {
      toast.error("Fehler beim Testen");
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Einstellungen
        </h1>
        <p className="text-slate-500 mt-1">
          App-Konfiguration und E-Mail-Benachrichtigungen
        </p>
      </div>

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList>
          <TabsTrigger value="email" className="gap-2">
            <Mail className="w-4 h-4" />
            E-Mail
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="w-4 h-4" />
            Benachrichtigungen
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Lock className="w-4 h-4" />
            Sicherheit
          </TabsTrigger>
        </TabsList>

        {/* Email Settings */}
        <TabsContent value="email">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                SMTP-Einstellungen
              </CardTitle>
              <CardDescription>
                E-Mail-Server für Benachrichtigungen konfigurieren
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP-Server</Label>
                  <Input
                    value={settings.smtp_host}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                    placeholder="smtp.example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                    placeholder="587"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Benutzername</Label>
                  <Input
                    value={settings.smtp_user}
                    onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passwort</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={settings.smtp_password}
                      onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                      placeholder="••••••••"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1 h-8 w-8"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Switch
                  checked={settings.smtp_ssl}
                  onCheckedChange={(checked) => setSettings({ ...settings, smtp_ssl: checked })}
                />
                <Label>SSL/TLS verwenden (Port 465)</Label>
              </div>

              <div className="space-y-2">
                <Label>Absender-E-Mail</Label>
                <Input
                  type="email"
                  value={settings.sender_email}
                  onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                  placeholder="snackstation@luenefire.de"
                />
              </div>

              <div className="space-y-2">
                <Label>Empfänger (kommagetrennt)</Label>
                <Input
                  value={settings.recipient_emails}
                  onChange={(e) => setSettings({ ...settings, recipient_emails: e.target.value })}
                  placeholder="admin@example.com, chef@example.com"
                />
              </div>

              <div className="flex gap-3">
                <Button onClick={handleSave} disabled={saving} className="bg-rose-600 hover:bg-rose-700">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Speichern..." : "Speichern"}
                </Button>
                <Button variant="outline" onClick={testEmail} disabled={testingEmail}>
                  <TestTube className="w-4 h-4 mr-2" />
                  {testingEmail ? "Teste..." : "Einstellungen testen"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Automatische Benachrichtigungen
              </CardTitle>
              <CardDescription>
                Cronjob-Einstellungen für regelmäßige E-Mail-Berichte
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Daily Report */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Tagesbericht</p>
                  <p className="text-sm text-slate-500">
                    Tägliche Zusammenfassung per E-Mail
                  </p>
                </div>
                <Switch
                  checked={settings.daily_report_enabled}
                  onCheckedChange={(checked) => 
                    setSettings({ ...settings, daily_report_enabled: checked })
                  }
                />
              </div>

              {/* Expiry Alert */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium">MHD-Warnung</p>
                  <p className="text-sm text-slate-500">
                    Warnung bei ablaufenden Produkten
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      min="1"
                      className="w-20 h-8"
                      value={settings.expiry_alert_days}
                      onChange={(e) => 
                        setSettings({ ...settings, expiry_alert_days: e.target.value })
                      }
                      disabled={!settings.expiry_alert_enabled}
                    />
                    <span className="text-sm text-slate-500">Tage vorher warnen</span>
                  </div>
                </div>
                <Switch
                  checked={settings.expiry_alert_enabled}
                  onCheckedChange={(checked) => 
                    setSettings({ ...settings, expiry_alert_enabled: checked })
                  }
                />
              </div>

              {/* Low Stock Alert */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Bestandswarnung</p>
                  <p className="text-sm text-slate-500">
                    Warnung bei niedrigem Lagerbestand
                  </p>
                </div>
                <Switch
                  checked={settings.low_stock_alert_enabled}
                  onCheckedChange={(checked) => 
                    setSettings({ ...settings, low_stock_alert_enabled: checked })
                  }
                />
              </div>

              {/* Cron Secret */}
              <div className="space-y-2">
                <Label>Cronjob-Secret</Label>
                <Input
                  value={settings.cron_secret}
                  onChange={(e) => setSettings({ ...settings, cron_secret: e.target.value })}
                  placeholder="geheimes-passwort"
                />
                <p className="text-xs text-slate-500">
                  Wird für den Aufruf der Cronjob-URLs benötigt
                </p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-blue-800 mb-2">Cronjob-URLs:</p>
                <div className="space-y-1 text-sm font-mono text-blue-700">
                  <p>/api/cron/daily-report?secret={settings.cron_secret || "xxx"}</p>
                  <p>/api/cron/expiry-alert?secret={settings.cron_secret || "xxx"}</p>
                </div>
              </div>

              <Button onClick={handleSave} disabled={saving} className="bg-rose-600 hover:bg-rose-700">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Speichern..." : "Speichern"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                App-Passwort
              </CardTitle>
              <CardDescription>
                Zugangskontrolle für die Anwendung
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Neues Passwort</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Neues Passwort eingeben"
                />
                <p className="text-xs text-slate-500">
                  Mindestens 4 Zeichen. Aktuelles Passwort: feuerwehr2024
                </p>
              </div>

              <Button 
                onClick={handlePasswordChange}
                disabled={!newPassword}
                className="bg-rose-600 hover:bg-rose-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                Passwort ändern
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
